/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box } from '@mui/material';

import ItemsSummary, { IItemsSummary } from './components/items-summary';
import DiscountSummary, {
	IDiscountSummary
} from './components/discount-summary';
import SubtotalSummary, {
	ISubtotalSummary
} from './components/subtotal-summary';
import TaxesAndChargesSummary, {
	ITaxesAndChargesSummary
} from './components/taxes-and-charges-summary';

import { useEffect, useState } from 'react';
import { useAuth } from '@erpsquad/common/runtime';
import TotalSummary, { ITotalSummary } from './components/total-summary';
import { calculateEntries, truncateDecimals } from '../../utils/calculation';
import { getApiConfigSafe } from '../../utils/api-config';

import './calculation-summary.scss';

interface ICalculationSummary {
	items: any;
	expenses?: any;
	formValues: any;
	enableShippings?: boolean;
	enablePromotions?: boolean;
	currencySymbol: string;
	decimals?: number;
	useTruncation?: boolean;
	// calculationFunc: any
}

const CalculationSummary: React.FC<ICalculationSummary> = ({
	items,
	expenses,
	formValues,
	enablePromotions,
	enableShippings,
	currencySymbol,
	decimals = 2,
	useTruncation = false
}) => {
	const { user } = useAuth();
	const defaultCurrencySymbol =
		currencySymbol || user?.currency_data?.symbol || '';

	const isArabia = getApiConfigSafe()?.countryEnv === 'ksa';
	const finalDecimals = isArabia ? 4 : decimals;
	const finalUseTruncation = isArabia ? true : useTruncation;

	const [itemsData, setItemsData] = useState<IItemsSummary>({
		totalQuantity: 0,
		total: 0,
		currencySymbol: defaultCurrencySymbol
	});
	const [discountData, setDiscountData] = useState<IDiscountSummary>({
		itemDiscount: 0,
		additionalDiscount: 0,
		totalDiscount: 0,
		currencySymbol: defaultCurrencySymbol
	});
	const [subtotalData, setSubtotalData] = useState<ISubtotalSummary>({
		subtotalExTax: 0,
		subtotalExDiscount: 0,
		currencySymbol: defaultCurrencySymbol
	});
	const [taxesData, setTaxesData] = useState<ITaxesAndChargesSummary>({
		taxes: 0,
		deductedTax: 0.0,
		total: 0,
		shippingAmount: 0,
		handlingAmount: 0,
		currencySymbol: defaultCurrencySymbol
	});
	const [totalData, setTotalData] = useState<ITotalSummary>({
		total: 0,
		roundOffDifferenceAmount: 0,
		currencySymbol: defaultCurrencySymbol
	});

	useEffect(() => {
		// debugger;
		const eRate = Number(formValues?.exchange_rate) || 1;

		let {
			// eslint-disable-next-line prefer-const
			totalQuantity,
			totalDiscount,
			totalItemsExpenseDiscount,
			totalTaxAmount,
			additinoalDiscountAmount,
			finalAmount,
			roundOffDifference,
			
		} = calculateEntries(
			items,
			expenses,
			formValues?.additional_discount_on,
			formValues?.additional_discount_percentage,
			formValues?.additional_discount_amount,
			formValues?.round_off,
			formValues?.round_off_option,
			formValues?.promotion_amount,
			finalDecimals,
			finalUseTruncation
		);

		totalDiscount /= eRate;
		additinoalDiscountAmount /= eRate;
		totalItemsExpenseDiscount /= eRate;
		totalTaxAmount /= eRate;
		finalAmount /= eRate;
		roundOffDifference /= eRate;
		
		const shippingAmount =
			(Number(formValues?.shipping_cost) || 0) +
			(Number(formValues?.shipping_tax_amount) || 0);
		const handlingAmount =
			(Number(formValues?.handling_cost) || 0) +
			(Number(formValues?.handling_tax_amount) || 0);

		const shippingAmountWithERate = shippingAmount / eRate;
		const handlingAmountWithERate = handlingAmount / eRate;
		const promotionAmountWithERate = Number(formValues?.promotion_amount || 0) / eRate;
		finalAmount = (finalAmount) - promotionAmountWithERate
		const grandTotal = (finalAmount + shippingAmountWithERate + handlingAmountWithERate) - promotionAmountWithERate

		const formatValue = (val: any) => {
			const num = Number(val) || 0;
			return finalUseTruncation ? truncateDecimals(num, finalDecimals).toFixed(finalDecimals) : num.toFixed(finalDecimals);
		};

		setItemsData({
			...itemsData,
			totalQuantity,
			total: formatValue(finalAmount),
			currencySymbol
		});
		setDiscountData({
			...discountData,
			itemDiscount: formatValue(totalItemsExpenseDiscount),
			additionalDiscount: formatValue(additinoalDiscountAmount),
			totalDiscount: formatValue(totalDiscount + promotionAmountWithERate),
			promotionAmount: formatValue(promotionAmountWithERate),
			currencySymbol
		});
		setSubtotalData({
			...subtotalData,
			subtotalExTax: formatValue(finalAmount - totalTaxAmount),
			subtotalExDiscount: formatValue(finalAmount + Number(totalDiscount)),
			currencySymbol
		});

		setTaxesData({
			...taxesData,
			taxes: formatValue(totalTaxAmount),
			total: formatValue(totalTaxAmount),
			shippingAmount: formatValue(shippingAmountWithERate),
			handlingAmount: formatValue(handlingAmountWithERate),
			currencySymbol
		});
		setTotalData({
			...totalData,
			total: formatValue(grandTotal),
			roundOffDifferenceAmount: formatValue(roundOffDifference),
			currencySymbol,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		items,
		formValues?.additional_discount_amount,
		formValues?.additional_discount_on,
		formValues?.additional_discount_percentage,
		formValues?.currency_id,
		formValues?.round_off,
		formValues?.round_off_option,
		formValues?.shipping_cost,
		formValues?.shipping_tax_amount,
		formValues?.handling_cost,
		formValues?.handling_tax_amount,
		formValues?.promotion_amount,
		formValues?.exchange_rate,
		formValues?.currency_data,
		defaultCurrencySymbol,
		finalDecimals,
		finalUseTruncation
	]);

	return (
		<Box className='addPurchaseInvoice--RightContainer'>
			<ItemsSummary data={itemsData} />
			<DiscountSummary
				data={discountData}
				enablePromotions={enablePromotions}
			/>
			<SubtotalSummary data={subtotalData} />
			<TaxesAndChargesSummary
				data={taxesData}
				enableShippings={enableShippings}
			/>
			<TotalSummary data={totalData} />
		</Box>
	);
};

export default CalculationSummary;
